import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageShell, redButton, goldButton } from '../components/PageShell';
import { useAuth } from '../context/AuthContext';
import { orderApi, formatApiError } from '../lib/orderApi';
import PrivacyPolicy from '../components/PrivacyPolicy';
import TermsOfService from '../components/TermsOfService';
import { CustomizeModal } from '../components/order/CustomizeModal';
import { TipSelector } from '../components/order/TipSelector';
import { ConsentBlock } from '../components/order/ConsentBlock';
import { DeliveryMenuItem } from '../components/order/DeliveryMenuItem';

const OrderPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const draft = (() => { try { return JSON.parse(localStorage.getItem('yamauchi_delivery') || '{}'); } catch { return {}; } })();

  const [config, setConfig] = useState(null);
  const [cart, setCart] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem('yamauchi_cart') || '[]'); return Array.isArray(c) ? c : []; } catch { return []; }
  });
  const [locationId, setLocationId] = useState(draft.locationId || '');
  const [notes, setNotes] = useState(draft.notes || '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState(draft.phone || '');
  const [guestMode, setGuestMode] = useState(false);
  const [guest, setGuest] = useState(draft.guest || { name: '', phone: '', email: '' });
  const [guestTerms, setGuestTerms] = useState(false);
  const [guestMarketing, setGuestMarketing] = useState(true);
  const [acctTerms, setAcctTerms] = useState(false);
  const [acctMarketing, setAcctMarketing] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const [customizing, setCustomizing] = useState(null);
  const [tipPct, setTipPct] = useState(0); // 0 | 10 | 15 | 20 | 'custom'
  const [tipCustom, setTipCustom] = useState('');

  useEffect(() => {
    orderApi.get('/delivery/config').then(({ data }) => setConfig(data)).catch(() => setError('No se pudo cargar el menú.'));
  }, []);

  useEffect(() => { localStorage.setItem('yamauchi_cart', JSON.stringify(cart)); }, [cart]);
  useEffect(() => {
    localStorage.setItem('yamauchi_delivery', JSON.stringify({ locationId, notes, phone, guest }));
  }, [locationId, notes, phone, guest]);

  const items = config?.items || [];
  const locations = config?.locations || [];
  const windowOpen = config?.window?.open;

  // ---------- cart helpers ----------
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const discPrice = (item) => (item.discount_pct > 0 ? round2(item.price * (1 - item.discount_pct / 100)) : item.price);
  const makeLine = (item, modIds, qty) => {
    const mods = modIds.map((mid) => (item.modifiers || []).find((m) => m.id === mid)).filter(Boolean).map((m) => ({ id: m.id, name: m.name.es, price: m.price }));
    const key = item.id + '|' + [...modIds].sort().join(',');
    return { key, id: item.id, name: item.name.es, base: discPrice(item), image: item.image, qty, mods, taxRate: item.tax_rate || 0 };
  };
  const lineUnit = (l) => l.base + l.mods.reduce((s, m) => s + m.price, 0);
  const orderDisc = config?.order_discount_pct || 0;
  const lineTotal = (l) => round2(lineUnit(l) * l.qty);
  const lineAfter = (l) => (orderDisc > 0 ? round2(lineTotal(l) * (1 - orderDisc / 100)) : lineTotal(l));
  const subtotalBefore = round2(cart.reduce((s, l) => s + lineTotal(l), 0));
  const subtotal = round2(cart.reduce((s, l) => s + lineAfter(l), 0));
  const discountAmt = round2(subtotalBefore - subtotal);
  const taxTotal = round2(cart.reduce((s, l) => s + round2(lineAfter(l) * (l.taxRate || 0)), 0));
  const tipAmount = tipPct === 'custom'
    ? round2(Math.max(0, parseFloat(tipCustom) || 0))
    : round2(subtotal * (tipPct / 100));
  const total = round2(subtotal + taxTotal + tipAmount);
  const itemCount = cart.reduce((s, l) => s + l.qty, 0);
  const simpleQty = (id) => { const l = cart.find((x) => x.key === id + '|'); return l ? l.qty : 0; };
  const changeSimple = (item, delta) => {
    setCart((prev) => {
      const key = item.id + '|';
      const idx = prev.findIndex((x) => x.key === key);
      const next = [...prev];
      if (idx >= 0) { const q = next[idx].qty + delta; if (q <= 0) next.splice(idx, 1); else next[idx] = { ...next[idx], qty: q }; }
      else if (delta > 0) next.push(makeLine(item, [], 1));
      return next;
    });
  };
  const addCustom = (item, modIds, qty) => {
    setCart((prev) => {
      const line = makeLine(item, modIds, qty);
      const idx = prev.findIndex((x) => x.key === line.key);
      const next = [...prev];
      if (idx >= 0) next[idx] = { ...next[idx], qty: next[idx].qty + qty }; else next.push(line);
      return next;
    });
    setCustomizing(null);
  };
  const removeLine = (key) => setCart((prev) => prev.filter((x) => x.key !== key));
  const changeLineQty = (key, delta) => {
    setCart((prev) => {
      const idx = prev.findIndex((x) => x.key === key);
      if (idx < 0) return prev;
      const next = [...prev];
      const q = next[idx].qty + delta;
      if (q <= 0) next.splice(idx, 1); else next[idx] = { ...next[idx], qty: q };
      return next;
    });
  };

  const checkout = async () => {
    setError('');
    if (itemCount === 0) { setError('Agrega al menos un artículo.'); return; }
    if (!locationId) { setError('Selecciona tu lugar de entrega.'); return; }

    const payload = {
      items: cart.map((l) => ({ id: l.id, qty: l.qty, modifiers: l.mods.map((m) => m.id) })),
      location_id: locationId, notes: notes.trim(), origin_url: window.location.origin,
      tip: tipAmount,
    };
    if (user) {
      if (!user.phone && !phone.trim()) { setError('Ingresa un teléfono para la entrega.'); return; }
      if (!user.phone) payload.phone = phone.trim();
      if (!user.terms_accepted) {
        if (!acctTerms) { setError('Debes aceptar los Términos de Uso y la Política de Privacidad para completar tu pedido.'); return; }
        payload.accept_terms = true;
        payload.marketing_opt_in = acctMarketing;
      }
    } else {
      if (!guest.name.trim() || !guest.phone.trim() || !guest.email.trim()) { setError('Completa tu nombre, teléfono y email de invitado.'); return; }
      if (!guestTerms) { setError('Debes aceptar los Términos de Uso y la Política de Privacidad para completar tu pedido.'); return; }
      payload.guest_name = guest.name.trim(); payload.guest_phone = guest.phone.trim(); payload.guest_email = guest.email.trim();
      payload.accept_terms = true;
      payload.marketing_opt_in = guestMarketing;
    }
    setBusy(true);
    try { const { data } = await orderApi.post('/orders', payload); window.location.href = data.checkout_url; }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); setBusy(false); }
  };

  let checkoutLabel = 'Pagar con tarjeta';
  if (!windowOpen) checkoutLabel = 'Pedidos cerrados';
  else if (busy) checkoutLabel = 'Procesando…';

  return (
    <PageShell>
      <div className="flex items-center" style={{ gap: '14px' }}>
        <span style={{ display: 'block', width: '32px', height: '1px', background: '#C9A84C' }} />
        <span className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '3px', color: '#C9A84C' }}>Delivery{config?.window?.delivery_day_name ? ` · ${config.window.delivery_day_name}` : ''}</span>
      </div>
      <h1 className="font-display mt-4" style={{ fontSize: 'clamp(32px,5vw,48px)', fontWeight: 700 }}>Pide tu Ramen</h1>

      {config && (
        <div className="mt-5 p-4" style={{ background: windowOpen ? 'rgba(201,168,76,0.08)' : '#141414', border: `1px solid ${windowOpen ? '#C9A84C' : 'rgba(201,168,76,0.2)'}` }} data-testid="window-banner">
          <p className="font-body" style={{ fontSize: '14px', color: windowOpen ? '#E8C96A' : '#C2C1B6', lineHeight: 1.6 }}>
            {windowOpen ? '🟢 ' : '🔴 '}{config.window.message}
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        {/* Menu */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((it) => (
            <DeliveryMenuItem
              key={it.id}
              it={it}
              discPrice={discPrice}
              qty={simpleQty(it.id)}
              onChangeQty={changeSimple}
              onCustomize={setCustomizing}
            />
          ))}
        </div>

        {/* Cart / checkout */}
        <div className="lg:col-span-1">
          <div className="p-5 lg:sticky lg:top-24" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.2)' }}>
            <h3 className="font-display" style={{ fontSize: '20px', fontWeight: 700 }}>Tu pedido</h3>
            <div className="mt-3 space-y-2">
              {itemCount === 0 && <p className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>Aún no has agregado nada.</p>}
              {cart.map((l) => (
                <div key={l.key} className="flex justify-between items-start gap-2 py-1" data-testid={`cart-line-${l.key}`}>
                  <div style={{ flex: 1 }}>
                    <div className="font-body" style={{ fontSize: '13px', color: '#F5F5F0' }}>{l.name}</div>
                    {l.mods.length > 0 && (<div className="font-body" style={{ fontSize: '11px', color: '#9A7522' }}>{l.mods.map((m) => m.name).join(', ')}</div>)}
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={() => changeLineQty(l.key, -1)} style={miniBtn} data-testid={`cart-minus-${l.key}`}>−</button>
                      <span className="font-bebas" style={{ fontSize: '15px', minWidth: '16px', textAlign: 'center' }} data-testid={`cart-qty-${l.key}`}>{l.qty}</span>
                      <button onClick={() => changeLineQty(l.key, 1)} style={miniBtn} data-testid={`cart-plus-${l.key}`}>+</button>
                      <button onClick={() => removeLine(l.key)} className="font-body" style={{ fontSize: '11px', color: '#E0736F', background: 'none', border: 'none', cursor: 'pointer', marginLeft: '6px' }} data-testid={`remove-${l.key}`}>Quitar</button>
                    </div>
                  </div>
                  <div className="text-right"><div className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>${(lineUnit(l) * l.qty).toFixed(2)}</div></div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 space-y-2" style={{ borderTop: '1px solid rgba(201,168,76,0.2)' }}>
              <div className="flex justify-between">
                <span className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>Subtotal</span>
                <span className="font-body" style={{ fontSize: '13px', color: '#F5F5F0' }} data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              {discountAmt > 0 && (
                <div className="flex justify-between mt-1">
                  <span className="font-body" style={{ fontSize: '13px', color: '#E0736F' }}>Descuento ({orderDisc}%)</span>
                  <span className="font-body" style={{ fontSize: '13px', color: '#E0736F' }} data-testid="cart-discount">-${discountAmt.toFixed(2)}</span>
                </div>
              )}
              {taxTotal > 0 && (
                <div className="flex justify-between">
                  <span className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>Impuesto (IVU)</span>
                  <span className="font-body" style={{ fontSize: '13px', color: '#F5F5F0' }} data-testid="cart-tax">${taxTotal.toFixed(2)}</span>
                </div>
              )}
              <TipSelector tipPct={tipPct} setTipPct={setTipPct} tipCustom={tipCustom} setTipCustom={setTipCustom} />
              {tipAmount > 0 && (
                <div className="flex justify-between">
                  <span className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>Propina</span>
                  <span className="font-body" style={{ fontSize: '13px', color: '#F5F5F0' }} data-testid="cart-tip">${tipAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2" style={{ borderTop: '1px solid rgba(201,168,76,0.12)' }}>
                <span className="font-body uppercase" style={{ fontSize: '12px', letterSpacing: '1px', color: '#C2C1B6' }}>Total a pagar</span>
                <span className="font-bebas" style={{ fontSize: '26px', color: '#C9A84C' }} data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Delivery location */}
            <label className="font-body uppercase block mt-5" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Lugar de entrega</label>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className="mt-1" style={{ ...cartInput, appearance: 'auto' }} data-testid="location-select">
              <option value="" disabled>Selecciona tu lugar de entrega</option>
              {locations.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
            </select>

            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas (opcional): salón, oficina, referencia…" className="mt-3" style={cartInput} data-testid="notes-input" />

            {user && !user.phone && (
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Teléfono para la entrega" className="mt-3" style={cartInput} data-testid="phone-input" />
            )}

            {user && !user.terms_accepted && (
              <div className="mt-4 space-y-3" data-testid="account-consent-block">
                <ConsentBlock
                  prefix="account"
                  terms={acctTerms} setTerms={setAcctTerms}
                  marketing={acctMarketing} setMarketing={setAcctMarketing}
                  onShowTerms={() => setShowTerms(true)} onShowPrivacy={() => setShowPrivacy(true)}
                />
              </div>
            )}

            {!user && (
              <div className="mt-4">
                {!guestMode ? (
                  <div className="space-y-2">
                    <button onClick={() => navigate('/login?next=/pedir')} style={{ ...goldButton, width: '100%' }} data-testid="go-login-btn">Entrar / Registrarme</button>
                    <button onClick={() => setGuestMode(true)} className="font-body" style={{ width: '100%', background: 'transparent', border: 'none', color: '#C2C1B6', fontSize: '13px', cursor: 'pointer', padding: '8px', textDecoration: 'underline' }} data-testid="guest-toggle">Continuar como invitado</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Datos de invitado</p>
                    <input value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="Nombre" style={cartInput} data-testid="guest-name" />
                    <input value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} placeholder="Teléfono" style={cartInput} data-testid="guest-phone" />
                    <input value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="Email (para el recibo)" style={cartInput} data-testid="guest-email" />
                    <ConsentBlock
                      prefix="guest"
                      terms={guestTerms} setTerms={setGuestTerms}
                      marketing={guestMarketing} setMarketing={setGuestMarketing}
                      onShowTerms={() => setShowTerms(true)} onShowPrivacy={() => setShowPrivacy(true)}
                    />
                  </div>
                )}
              </div>
            )}

            {error && <p className="font-body mt-3" style={{ fontSize: '12px', color: '#E0736F' }} data-testid="order-error">{error}</p>}

            {(user || guestMode) && (
              <button onClick={checkout} disabled={busy || !windowOpen} style={{ ...redButton, width: '100%', marginTop: '16px', opacity: (busy || !windowOpen) ? 0.5 : 1, cursor: (busy || !windowOpen) ? 'not-allowed' : 'pointer' }} data-testid="checkout-btn">
                {checkoutLabel}
              </button>
            )}
            {(user || guestMode) && (
              <p className="font-body mt-3" style={{ fontSize: '11px', color: '#C2C1B6', lineHeight: 1.5 }}>
                Al pagar aceptas los Términos de Servicio y la Política de Privacidad de Yamauchi Ramen. El pago se procesa de forma segura con Stripe.
              </p>
            )}
          </div>
        </div>
      </div>

      {customizing && (
        <CustomizeModal item={customizing} onAdd={addCustom} onClose={() => setCustomizing(null)} />
      )}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
    </PageShell>
  );
};

const miniBtn = { width: '24px', height: '24px', border: '1px solid rgba(201,168,76,0.4)', color: '#C9A84C', background: 'transparent', fontSize: '14px', lineHeight: 1, cursor: 'pointer' };
const cartInput = { width: '100%', background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.25)', color: '#F5F5F0', padding: '11px 13px', fontSize: '13px', outline: 'none' };

export default OrderPage;
